import { useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import type { DayPart } from "./TodayCapture";

// The Today companion: an owl on a branch beside the text box. It is present
// but quiet — it breathes, blinks, now and then tilts its head; while you write
// it turns toward the box ("listening"); when a note is saved it hops and
// flutters ("cheer"). One short line of encouragement sits above it.
// The drawing is decorative (aria-hidden); the line is plain text.

export type OwlMood = "idle" | "listening" | "cheer";

/** The line above the owl: what you're doing first, then the time of day. */
export const owlLineKey = (part: DayPart, mood: OwlMood): string =>
  mood === "cheer" ? "todayHome.owl.cheer" : mood === "listening" ? "todayHome.owl.listening" : `todayHome.owl.${part}`;

const CHEER_MS = 5000;

const CSS = `
.owl *{transform-box:fill-box}
@keyframes owl-breathe{0%,100%{transform:scale(1)}50%{transform:scale(1.015,1.02)}}
@keyframes owl-blink{0%,92%,100%{transform:scaleY(1)}95%{transform:scaleY(.08)}}
@keyframes owl-tilt{0%,84%,100%{transform:rotate(0)}88%,94%{transform:rotate(-7deg)}}
@keyframes owl-hop{0%,100%{transform:translateY(0)}30%{transform:translateY(-10px)}55%{transform:translateY(0)}70%{transform:translateY(-4px)}}
@keyframes owl-flap-l{0%,100%{transform:rotate(0)}20%,60%{transform:rotate(22deg)}40%,80%{transform:rotate(4deg)}}
@keyframes owl-flap-r{0%,100%{transform:rotate(0)}20%,60%{transform:rotate(-22deg)}40%,80%{transform:rotate(-4deg)}}
@keyframes owl-spark{0%{opacity:0;transform:scale(0)}40%{opacity:1;transform:scale(1)}100%{opacity:0;transform:scale(.5)}}
@keyframes owl-twinkle{0%,100%{opacity:.3}50%{opacity:1}}
@keyframes owl-in{0%{opacity:0;transform:translateY(6px)}100%{opacity:1;transform:none}}
.owl-breathe{animation:owl-breathe 4.5s ease-in-out infinite;transform-origin:50% 100%}
.owl-eye{animation:owl-blink 6s ease-in-out infinite;transform-origin:50% 50%}
.owl-eye.r{animation-delay:.04s}
.owl-head{transform-origin:50% 85%;transition:transform .35s cubic-bezier(.3,.7,.4,1)}
.owl[data-mood=idle] .owl-head{animation:owl-tilt 11s ease-in-out 2s infinite}
.owl[data-mood=listening] .owl-head{transform:rotate(6deg)}
.owl-pupil{transition:transform .35s cubic-bezier(.3,.7,.4,1)}
.owl[data-mood=listening] .owl-pupil{transform:translate(2.6px,1.4px)}
.owl[data-mood=cheer] .owl-hop{animation:owl-hop .9s cubic-bezier(.3,.7,.4,1) 1}
.owl[data-mood=cheer] .owl-wing.l{animation:owl-flap-l .9s ease-in-out 2;transform-origin:80% 10%}
.owl[data-mood=cheer] .owl-wing.r{animation:owl-flap-r .9s ease-in-out 2;transform-origin:20% 10%}
.owl[data-mood=cheer] .owl-spark{animation:owl-spark 1.1s ease-out both}
.owl-spark{opacity:0}
.owl-star{animation:owl-twinkle 3s ease-in-out infinite}
.owl-bubble{animation:owl-in .35s cubic-bezier(.3,.7,.4,1) both}
@media (prefers-reduced-motion:reduce){.owl *,.owl-bubble{animation:none!important;transition:none!important}}
`;

const BELLY_ROWS = [
  { y: 98, xs: [58, 66, 74] },
  { y: 106, xs: [54, 62, 70, 78] },
  { y: 114, xs: [52, 60, 68, 76, 84] },
  { y: 122, xs: [55, 63, 71, 79] },
  { y: 130, xs: [60, 68, 76] },
];

interface Props {
  part: DayPart;
  mood: OwlMood;
  /** Show the line of encouragement above the owl (wide screens). */
  bubble?: boolean;
  className?: string;
}

const TodayOwl = ({ part, mood, bubble = false, className }: Props) => {
  const { t } = useTranslation();
  const uid = useId().replace(/:/g, "");
  const id = (n: string) => `${uid}-${n}`;
  const line = t(owlLineKey(part, mood));
  const evening = part === "evening";

  return (
    <div className={cn("owl-wrap flex flex-col items-center", className)}>
      <style>{CSS}</style>
      {bubble && (
        <p
          key={line}
          dir="auto"
          data-testid="owl-line"
          className="owl-bubble relative mb-2 w-max max-w-[150px] rounded-2xl border border-border/70 bg-card px-3 py-2 text-center text-[13px] leading-snug text-foreground shadow-sm"
        >
          {line}
          <span
            aria-hidden
            className="absolute -bottom-[5px] start-1/2 h-2.5 w-2.5 -translate-x-1/2 rotate-45 border-b border-e border-border/70 bg-card rtl:translate-x-1/2"
          />
        </p>
      )}
      <svg
        viewBox="0 0 140 160"
        aria-hidden
        focusable="false"
        data-testid="today-owl"
        data-mood={mood}
        className="owl h-full w-full overflow-visible rtl:-scale-x-100"
      >
        <defs>
          <radialGradient id={id("body")} cx="50%" cy="38%" r="70%">
            <stop offset="0" stopColor="#B07F55" />
            <stop offset=".65" stopColor="#8A5D3B" />
            <stop offset="1" stopColor="#6A452B" />
          </radialGradient>
          <linearGradient id={id("wing")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#6E4A2F" />
            <stop offset="1" stopColor="#4F3320" />
          </linearGradient>
          <radialGradient id={id("belly")} cx="50%" cy="30%" r="75%">
            <stop offset="0" stopColor="#FBF1E0" />
            <stop offset="1" stopColor="#EAD3B1" />
          </radialGradient>
          <radialGradient id={id("disc")} cx="50%" cy="45%" r="60%">
            <stop offset="0" stopColor="#FFF8EE" />
            <stop offset="1" stopColor="#EEDDC3" />
          </radialGradient>
          <radialGradient id={id("iris")} cx="42%" cy="38%" r="65%">
            <stop offset="0" stopColor="#FFD77A" />
            <stop offset=".6" stopColor="#F0A93A" />
            <stop offset="1" stopColor="#C97A1E" />
          </radialGradient>
          <linearGradient id={id("branch")} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#8C6239" />
            <stop offset="1" stopColor="#5E3F22" />
          </linearGradient>
          <radialGradient id={id("moon")} cx="40%" cy="40%" r="60%">
            <stop offset="0" stopColor="#FFF4D2" />
            <stop offset="1" stopColor="#F6D88E" />
          </radialGradient>
        </defs>

        {evening && (
          <g>
            <circle cx="112" cy="26" r="14" fill={`url(#${id("moon")})`} opacity=".9" />
            <circle className="owl-star" cx="24" cy="18" r="1.6" fill="#F6D88E" />
            <circle className="owl-star" cx="38" cy="8" r="1.1" fill="#F6D88E" style={{ animationDelay: "1s" }} />
            <circle className="owl-star" cx="128" cy="56" r="1.2" fill="#F6D88E" style={{ animationDelay: "2s" }} />
          </g>
        )}

        {/* Branch */}
        <path d="M2 146 C 40 138, 96 140, 138 132" stroke={`url(#${id("branch")})`} strokeWidth="7" strokeLinecap="round" fill="none" />
        <path d="M110 136 c 6 -8 16 -9 22 -6 c -6 6 -14 8 -22 6z" fill="#6FAE7E" />
        <path d="M118 135 c 3 -3 8 -4 12 -3" stroke="#4E8A5E" strokeWidth="1" fill="none" />
        <path d="M14 144 c -4 -8 -1 -16 4 -19 c 3 7 1 14 -4 19z" fill="#8CC29A" />

        <g className="owl-hop">
          {/* Feet */}
          {[58, 82].map((x) => (
            <g key={x} fill="#E3A048">
              <path d={`M${x - 5} 139 q 1 5 -1 7 h 3 q 1 -3 1 -7z`} />
              <path d={`M${x - 1} 139 q 1 6 0 8 h 3 q 0 -4 0 -8z`} />
              <path d={`M${x + 3} 139 q 2 5 2 7 h 3 q -1 -4 -2 -7z`} />
            </g>
          ))}

          <g className="owl-breathe">
            {/* Body */}
            <path
              d="M70 34 C 101 34 113 62 111 96 C 109 125 93 141 70 141 C 47 141 31 125 29 96 C 27 62 39 34 70 34 Z"
              fill={`url(#${id("body")})`}
            />
            {/* Belly with scalloped feathers */}
            <path d="M70 84 C 90 84 98 100 97 116 C 96 132 85 140 70 140 C 55 140 44 132 43 116 C 42 100 50 84 70 84 Z" fill={`url(#${id("belly")})`} />
            {BELLY_ROWS.map((row) =>
              row.xs.map((x) => (
                <path key={`${x}-${row.y}`} d={`M${x - 3} ${row.y} q 3 3.4 6 0`} stroke="#C9A178" strokeWidth="1.1" fill="none" strokeLinecap="round" opacity=".85" />
              ))
            )}

            {/* Wings */}
            <g className="owl-wing l">
              <path d="M34 74 C 22 92 22 120 38 134 C 44 126 46 112 46 98 C 46 88 42 80 34 74 Z" fill={`url(#${id("wing")})`} />
              <path d="M30 104 q 6 4 12 2 M30 114 q 6 4 12 2 M32 124 q 5 3 10 1" stroke="#3D2717" strokeWidth="1" fill="none" opacity=".55" strokeLinecap="round" />
            </g>
            <g className="owl-wing r">
              <path d="M106 74 C 118 92 118 120 102 134 C 96 126 94 112 94 98 C 94 88 98 80 106 74 Z" fill={`url(#${id("wing")})`} />
              <path d="M110 104 q -6 4 -12 2 M110 114 q -6 4 -12 2 M108 124 q -5 3 -10 1" stroke="#3D2717" strokeWidth="1" fill="none" opacity=".55" strokeLinecap="round" />
            </g>

            {/* Head */}
            <g className="owl-head">
              <path d="M44 48 L 34 20 C 42 24 52 32 58 40 Z" fill="#5C3D26" />
              <path d="M96 48 L 106 20 C 98 24 88 32 82 40 Z" fill="#5C3D26" />
              {/* Facial disc */}
              <path d="M70 52 C 62 46 46 46 38 56 C 30 68 34 86 50 90 C 60 92 66 88 70 84 C 74 88 80 92 90 90 C 106 86 110 68 102 56 C 94 46 78 46 70 52 Z" fill={`url(#${id("disc")})`} />
              <path d="M70 52 C 62 46 46 46 38 56 C 30 68 34 86 50 90 C 60 92 66 88 70 84 C 74 88 80 92 90 90 C 106 86 110 68 102 56 C 94 46 78 46 70 52 Z" fill="none" stroke="#D8BE98" strokeWidth="1.2" />
              {/* Eyes */}
              {[
                { cx: 54, cls: "l" },
                { cx: 86, cls: "r" },
              ].map(({ cx, cls }) => (
                <g key={cls} className={`owl-eye ${cls}`}>
                  <circle cx={cx} cy="68" r="12.5" fill="#2A1C12" opacity=".12" />
                  <circle cx={cx} cy="67" r="11.5" fill={`url(#${id("iris")})`} />
                  <g className="owl-pupil">
                    <circle cx={cx} cy="67" r="6.2" fill="#1C120B" />
                    <circle cx={cx - 3} cy="63.6" r="2.5" fill="#fff" />
                    <circle cx={cx + 2.6} cy="70" r="1.1" fill="#fff" opacity=".8" />
                  </g>
                </g>
              ))}
              {/* Brow ridge */}
              <path d="M44 55 Q 54 50 64 56 M76 56 Q 86 50 96 55" stroke="#B48A5F" strokeWidth="1.4" fill="none" strokeLinecap="round" opacity=".7" />
              {/* Beak */}
              <path d="M65 77 Q 70 74 75 77 L 70.6 88 Q 70 89 69.4 88 Z" fill="#E3A048" />
              <path d="M65 77 Q 70 74 75 77" stroke="#B87A2E" strokeWidth="1" fill="none" />
              {/* Cheeks */}
              <ellipse cx="45" cy="80" rx="5" ry="3" fill="#F2A88A" opacity=".35" />
              <ellipse cx="95" cy="80" rx="5" ry="3" fill="#F2A88A" opacity=".35" />
            </g>
          </g>
        </g>

        {/* Cheer sparkles */}
        {[
          [22, 70, 0],
          [118, 64, 0.15],
          [112, 100, 0.3],
        ].map(([x, y, d], i) => (
          <path
            key={i}
            className="owl-spark"
            d={`M${x} ${y - 5} V${y + 5} M${x - 5} ${y} H${x + 5}`}
            stroke="#F0A93A"
            strokeWidth="2"
            strokeLinecap="round"
            style={{ animationDelay: `${d}s` }}
          />
        ))}
      </svg>
    </div>
  );
};

/** Turns a save count into a short cheer, then back to the resting mood. */
export function useOwlMood(listening: boolean, cheers: number): OwlMood {
  const [cheering, setCheering] = useState(false);
  useEffect(() => {
    if (!cheers) return;
    setCheering(true);
    const timer = setTimeout(() => setCheering(false), CHEER_MS);
    return () => clearTimeout(timer);
  }, [cheers]);
  if (cheering) return "cheer";
  return listening ? "listening" : "idle";
}

export default TodayOwl;
